/**
 * @file        src/modals/HighlightsModal.jsx
 * @description "Highlights" modal — lists the property's live facilities/
 *              amenities. Opened via nh:open-modal { name: 'highlights',
 *              payload: { facilities:[name], name } }. Renders only real
 *              supplier facility names (no fabricated stats or stock images).
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-06-22 Claude — Initial port. Static HIGHLIGHTS data set.
 *   - 2026-06-24 Claude — Made dynamic from the live hotel facilities; dropped
 *                         the static data set, fake descriptions and images.
 */
import { useEffect } from 'react';
import { amenityIcon } from '../lib/amenityIcons.js';

export default function HighlightsModal({ open, onClose, payload }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  // De-duplicate (case-insensitive) — the supplier list often repeats entries
  // like "Spa" / "Laundry Facilities".
  const facilities = (() => {
    const seen = new Set();
    return (Array.isArray(payload?.facilities) ? payload.facilities : [])
      .filter(Boolean)
      .filter((f) => {
        const k = String(f).trim().toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
  })();
  const name = payload?.name || 'this property';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-xl max-h-[88svh] overflow-y-auto rounded-3xl border border-line bg-panel no-scrollbar">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line sticky top-0 bg-panel z-10">
          <div className="min-w-0">
            <h2 className="font-display text-2xl font-extrabold">Highlights</h2>
            <p className="text-muted text-xs mt-0.5 truncate">Facilities at {name}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer shrink-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
          </button>
        </div>
        <div className="p-6">
          {facilities.length ? (
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
              {facilities.map((f) => (
                <div key={f} className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-lg bg-neon/10 text-neon flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d={amenityIcon(f)} /></svg>
                  </span>
                  <p className="font-semibold text-sm pt-1.5">{f}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted">No facility details are available for this property.</p>
          )}
        </div>
      </div>
    </div>
  );
}
